// Delegado de Protección de Datos (DPO) — LOPDP Art. 48.
// Sistemas → Gestión de Autenticación → Delegado de Protección. Solo admin (nivel>=5).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Switch, fieldCls } from './shared';

interface Dpo {
  id?: string; nombre?: string; identificacion?: string; email?: string; telefono?: string;
  direccion?: string; es_externo?: boolean; organizacion?: string; fecha_designacion?: string;
  funciones?: string; activo?: boolean;
}
const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

const vacio: Dpo = { nombre: '', identificacion: '', email: '', telefono: '', direccion: '', es_externo: false, organizacion: '', fecha_designacion: '', funciones: '', activo: true };

export function Delegado() {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<Dpo | null>({ queryKey: ['dpo'], queryFn: () => rpc<Dpo | null>('dpo_obtener') });
  const [form, setForm] = useState<Dpo>(vacio);
  useEffect(() => { if (data) setForm({ ...vacio, ...data, fecha_designacion: data.fecha_designacion ?? '' }); }, [data]);
  const set = (k: keyof Dpo, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = useMutation({
    mutationFn: () => rpc('dpo_guardar', { _data: form }),
    onSuccess: () => { toast.success('Delegado de Protección guardado'); void qc.invalidateQueries({ queryKey: ['dpo'] }); void qc.invalidateQueries({ queryKey: ['aviso-privacidad'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><UserCheck className="w-5 h-5 text-primary" /> Delegado de Protección de Datos</h1>
        <p className="text-sm text-muted-foreground">Designación del DPO (LOPDP Art. 48). Obligatorio al tratar datos sensibles a gran escala (p. ej. datos de salud). Su contacto se informa al titular.</p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Nombre completo *"><input className={fieldCls} value={form.nombre ?? ''} onChange={(e) => set('nombre', e.target.value)} /></Campo>
            <Campo label="Identificación"><input className={fieldCls} value={form.identificacion ?? ''} onChange={(e) => set('identificacion', e.target.value)} /></Campo>
            <Campo label="Correo electrónico"><input className={fieldCls} value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Campo>
            <Campo label="Teléfono"><input className={fieldCls} value={form.telefono ?? ''} onChange={(e) => set('telefono', e.target.value)} /></Campo>
            <Campo label="Dirección" className="col-span-2"><input className={fieldCls} value={form.direccion ?? ''} onChange={(e) => set('direccion', e.target.value)} /></Campo>
            <Campo label="Fecha de designación"><input type="date" className={fieldCls} value={form.fecha_designacion ?? ''} onChange={(e) => set('fecha_designacion', e.target.value)} /></Campo>
            <div className="flex items-end gap-4 pb-2">
              <Switch checked={!!form.es_externo} onChange={(v) => set('es_externo', v)} label="Delegado externo" />
              <Switch checked={form.activo !== false} onChange={(v) => set('activo', v)} label="Activo" />
            </div>
            {form.es_externo && <Campo label="Organización (si es externo)" className="col-span-2"><input className={fieldCls} value={form.organizacion ?? ''} onChange={(e) => set('organizacion', e.target.value)} /></Campo>}
            <Campo label="Funciones / observaciones" className="col-span-2"><textarea className={`${fieldCls} h-24`} value={form.funciones ?? ''} onChange={(e) => set('funciones', e.target.value)} /></Campo>
          </div>
          <div className="flex justify-end">
            <Button isLoading={guardar.isPending} disabled={!form.nombre?.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
